import React, { FC, useEffect, useMemo, useState } from 'react';
import { formatNumberDecimal } from '../../../../_mexablock/helpers';
import { getBalanceAprov } from '../../../service/dashboard/Dashboard';

type Props = {
  title?: string;
  subtitle?: string;
  currentLabel?: string;
  previousLabel?: string;
};

const percent = (consumido: number, aprovisionado: number) => {
  if (!aprovisionado || aprovisionado <= 0) return 0;
  return Math.min(100, Math.max(0, (consumido / aprovisionado) * 100));
};

export const ProvisionBalanceCard: FC<Props> = ({
  title = 'Balance de Aprovisionamiento',
  subtitle = 'Relación de saldo aprovisionado y consumo real',
  currentLabel = 'Mes actual',
  previousLabel = 'Mes anterior',
}) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    totalAprovisionado: 0,
    currentAprovisionado: 0,
    currentConsumido: 0,
    previousAprovisionado: 0,
    previousConsumido: 0,
  });

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const response = await getBalanceAprov();
        setData({
          totalAprovisionado: response.totalGeneralAprovisionado ?? 0,
          currentAprovisionado: response.mesActual ?? 0,
          currentConsumido: response.consumoMesActual ?? 0,
          previousAprovisionado: response.mesAnterior ?? 0,
          previousConsumido: response.consumoMesAnterior ?? 0,
        });
      } catch (error) {
        console.error('Error fetching balance:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBalance();
  }, []);

  const currentUse = useMemo(
    () => percent(data.currentConsumido, data.currentAprovisionado),
    [data.currentConsumido, data.currentAprovisionado],
  );

  const prevUse = useMemo(
    () => percent(data.previousConsumido, data.previousAprovisionado),
    [data.previousConsumido, data.previousAprovisionado],
  );

  if (loading) {
    return (
      <div className="card card-flush h-100">
        <div className="card-body d-flex align-items-center justify-content-center">
          <span className="spinner-border text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="card card-flush h-100">
      <div className="card-header pt-5">
        <div className="card-title d-flex flex-column">
          <span className="fs-3 fw-bold text-gray-900">{title}</span>
          <span className="text-muted fw-semibold fs-7">{subtitle}</span>
        </div>
      </div>

      <div className="card-body pt-6">
        {/* Total aprovisionado */}
        <div className="text-center mb-8">
          <span className="text-primary fw-semibold fs-5">
            Total aprovisionado
          </span>
          <div className="d-flex align-items-center justify-content-center mt-2">
            <span className="fs-2hx fw-semibold text-primary me-1 align-self-start">
              $
            </span>
            <span className="fs-2hx fw-bolder text-primary lh-1 ls-n2">
              {formatNumberDecimal(data.totalAprovisionado)}
            </span>
          </div>
        </div>

        <div className="separator separator-dashed my-6"></div>

        {/* Comparación */}
        <div className="row g-5">
          {/* Mes actual */}
          <div className="col-12 col-md-6">
            <div className="text-center">
              <span className="text-primary fw-bold fs-4 d-block mb-4">
                {currentLabel}
              </span>

              <div className="mb-4">
                <span className="text-primary fw-semibold fs-6 d-block">
                  Aprovisionado
                </span>
                <div className="d-flex align-items-center justify-content-center">
                  <span className="fs-4 fw-semibold text-gray-500 me-1 align-self-start">
                    $
                  </span>
                  <span className="fs-2x fw-bold text-primary lh-1">
                    {formatNumberDecimal(data.currentAprovisionado)}
                  </span>
                </div>
              </div>

              <div className="mb-4">
                <span className="text-primary fw-semibold fs-6 d-block">
                  Consumido
                </span>
                <div className="d-flex align-items-center justify-content-center">
                  <span className="fs-4 fw-semibold text-gray-500 me-1 align-self-start">
                    $
                  </span>
                  <span className="fs-2x fw-bold text-primary lh-1">
                    {formatNumberDecimal(data.currentConsumido)}
                  </span>
                </div>
              </div>

              {/* Progress */}
              <div className="mt-5">
                <div className="progress h-8px bg-light-primary rounded">
                  <div
                    className="progress-bar bg-primary rounded"
                    role="progressbar"
                    style={{ width: `${currentUse.toFixed(1)}%` }}
                    aria-valuenow={currentUse}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
                <span className="text-gray-500 fw-semibold fs-7 mt-2 d-block">
                  % de uso: {currentUse.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          {/* Mes anterior */}
          <div className="col-12 col-md-6">
            <div className="text-center">
              <span className="text-gray-700 fw-bold fs-4 d-block mb-4">
                {previousLabel}
              </span>

              <div className="mb-4">
                <span className="text-gray-600 fw-semibold fs-6 d-block">
                  Aprovisionado
                </span>
                <div className="d-flex align-items-center justify-content-center">
                  <span className="fs-4 fw-semibold text-gray-500 me-1 align-self-start">
                    $
                  </span>
                  <span className="fs-2x fw-bold text-gray-700 lh-1">
                    {formatNumberDecimal(data.previousAprovisionado)}
                  </span>
                </div>
              </div>

              <div className="mb-4">
                <span className="text-gray-600 fw-semibold fs-6 d-block">
                  Consumido
                </span>
                <div className="d-flex align-items-center justify-content-center">
                  <span className="fs-4 fw-semibold text-gray-500 me-1 align-self-start">
                    $
                  </span>
                  <span className="fs-2x fw-bold text-gray-700 lh-1">
                    {formatNumberDecimal(data.previousConsumido)}
                  </span>
                </div>
              </div>

              {/* Progress */}
              <div className="mt-5">
                <div className="progress h-8px bg-light rounded">
                  <div
                    className="progress-bar bg-gray-600 rounded"
                    role="progressbar"
                    style={{ width: `${prevUse.toFixed(1)}%` }}
                    aria-valuenow={prevUse}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
                <span className="text-gray-500 fw-semibold fs-7 mt-2 d-block">
                  % de uso: {prevUse.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
